'use client'

import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useReadonly } from "@/contexts/readonlyContext";

export type MeTabEditModalType = 'whoAmI' | 'myMission' | null;

type Props = {
    show: boolean;
    onHide: () => void;
    type: MeTabEditModalType;
    whoAmI: string;
    myMission: string;
    whoAmIRating: number;
    myMissionRating: number;
    onSave: (data: {
        whoAmI?: string;
        myMission?: string;
        whoAmIRating?: number;
        myMissionRating?: number;
    }) => void;
};

export default function MeTabEditModal({
    show,
    onHide,
    type,
    whoAmI,
    myMission,
    whoAmIRating,
    myMissionRating,
    onSave,
}: Props) {
    const { readonly } = useReadonly();
    const [localWhoAmI, setLocalWhoAmI] = useState(whoAmI);
    const [localMyMission, setLocalMyMission] = useState(myMission);
    const [localWhoAmIRating, setLocalWhoAmIRating] = useState(whoAmIRating);
    const [localMyMissionRating, setLocalMyMissionRating] = useState(myMissionRating);

    useEffect(() => {
        setLocalWhoAmI(whoAmI);
        setLocalMyMission(myMission);
        setLocalWhoAmIRating(whoAmIRating);
        setLocalMyMissionRating(myMissionRating);
    }, [whoAmI, myMission, whoAmIRating, myMissionRating, show]);

    const handleSave = () => {
        if (readonly) return;
        onSave({
            whoAmI: localWhoAmI,
            myMission: localMyMission,
            whoAmIRating: localWhoAmIRating,
            myMissionRating: localMyMissionRating,
        });
        onHide();
    };

    const isWhoAmI = type === 'whoAmI';
    const isMyMission = type === 'myMission';

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {isWhoAmI && 'Редактировать: Кто я'}
                    {isMyMission && 'Редактировать: Моя миссия'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    {isWhoAmI && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label><strong>Кто я</strong></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    value={localWhoAmI}
                                    onChange={e => setLocalWhoAmI(e.target.value)}
                                    placeholder="Опишите, кто вы..."
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label><small>Насколько эта формулировка вам отзывается:</small></Form.Label>
                                <div className="d-flex gap-1">
                                    {Array.from({ length: 10 }, (_, i) => i + 1).map((value) => (
                                        <Button
                                            key={value}
                                            variant={value <= localWhoAmIRating ? "primary" : "outline-primary"}
                                            size="sm"
                                            onClick={() => setLocalWhoAmIRating(value)}
                                            style={{ flex: 1 }}
                                        >
                                            {value}
                                        </Button>
                                    ))}
                                </div>
                            </Form.Group>
                        </>
                    )}
                    {isMyMission && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label><strong>Моя миссия</strong></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={8}
                                    value={localMyMission}
                                    onChange={e => setLocalMyMission(e.target.value)}
                                    placeholder="Опишите свою миссию..."
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label><small>Насколько эта формулировка вам отзывается:</small></Form.Label>
                                <div className="d-flex gap-1">
                                    {Array.from({ length: 10 }, (_, i) => i + 1).map((value) => (
                                        <Button
                                            key={value}
                                            variant={value <= localMyMissionRating ? "primary" : "outline-primary"}
                                            size="sm"
                                            onClick={() => setLocalMyMissionRating(value)}
                                            style={{ flex: 1 }}
                                        >
                                            {value}
                                        </Button>
                                    ))}
                                </div>
                            </Form.Group>
                        </>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Отмена
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Сохранить
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
